/**
 * The Rules room: what is worth watching, written down.
 *
 * Two states, one component: the list, and the form over it. The form is
 * deliberately plain - eight slots is the shape `docs/plans/argus.md` settles on, and V1
 * fills five of them. What it does carry is the sentence, live, above the
 * fields: the rule people argue about in a meeting is that line, not the form
 * that produced it.
 *
 * Three honesty rules are visible here rather than assumed:
 *
 * - Every rule is a **draft** and says so, because nothing deploys yet and the
 *   database refuses the state rather than trusting this screen.
 * - A reference beyond a fixed number is offered as **unavailable with a
 *   reason**, not hidden: the team should see the ladder they will climb.
 * - A rule nobody is evaluating says exactly that, on every row.
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { directive, Directive, PartType, type ElementPart, type PartInfo } from 'lit/directive.js';
import { keyed } from 'lit/directives/keyed.js';
import { repeat } from 'lit/directives/repeat.js';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import { canDo, myUserId } from '../../state/store.ts';
import type {
  ArgusAggregate,
  ArgusSeverity,
  ArgusGate,
  ArgusRule,
  ArgusRuleDocument,
  ArgusShape,
} from '../../api/types.ts';
import { ruleSentence, SHAPE_WORDS } from './argus-sentence.ts';
import { paginate, renderPager } from './argus-pager.ts';
import './argus-rule-blocks.ts';
import './argus-rule-editor.ts';
import '../../ui/hold-switch.ts';
import { argusRoomReselected, setArgusRoom } from './argus-lens.ts';
import { inboxRuleFilter, rulesRoomOpenRule } from './argus-inbox-store.ts';
import { plural } from './argus-backtest-panel.ts';
import './argus-schedule-panel.ts';
import './argus-delete-strip.ts';
import './argus-team-rail.ts';
import { teamHue, teamInitials } from './argus-team-rail.ts';
import { showToast } from '../../ui/toast.ts';
import {
  assistantDraft,
  type AssistantRuleDraft,
  findings,
  loadFindings,
  loadWatchers,
  entities,
  loadEntities,
  loadRules,
  loadSeries,
  loadSources,
  removeRule,
  rules,
  setStatus,
  rulesError,
  rulesStatus,
  saveRule,
  seriesForEntity,
  sources,
  teams,
  teamsLoaded,
  inNoTeam,
  loadTeams,
  newRulePlace,
  moveRuleToFolder,
  setVisibility,
  setFollow,
} from './argus-rules-store.ts';

const WINDOWS = ['1h', '6h', '12h', '24h', '3d', '7d', '30d'] as const;

type StateFilter = 'all' | 'deployed' | 'draft' | 'retired';

/** What the watcher has to say about a running rule, as a filter. */
type FoundFilter = 'alerting' | 'quiet' | 'trouble';

const PAGE_SIZES = [25, 50, 100];
const PAGE_SIZE_KEY = 'argus.rules.pageSize';
/** The rail's place, remembered in this browser (decision 35). */
const PLACE_KEY = 'argus.rules.place';

function readPlace(): string {
  try {
    return localStorage.getItem(PLACE_KEY) ?? '';
  } catch {
    return '';
  }
}

function readPageSize(): number {
  try {
    const n = Number(localStorage.getItem(PAGE_SIZE_KEY));
    return PAGE_SIZES.includes(n) ? n : PAGE_SIZES[0]!;
  } catch {
    return PAGE_SIZES[0]!;
  }
}

/**
 * A deployed rule's standing with the watcher, for the Found filter: alerting
 * (something open), trouble (nobody is evaluating it - unclaimed, refused, or
 * its watcher has gone quiet), or quiet (watched, nothing open). Null for a
 * rule that is not running: it has nothing to be found.
 */
function foundOf(rule: ArgusRule): FoundFilter | null {
  if (rule.status !== 'deployed') return null;
  const watch = rule.watch;
  const open = (watch?.needs_look ?? 0) > 0;
  // Quiet with something still open is both; the open alert matters more.
  if (open) return 'alerting';
  if (!watch || watch.state === 'unclaimed' || watch.state === 'refused' || watch.state === 'quiet') return 'trouble';
  return 'quiet';
}

/** The drawer's width in pixels, as last dragged - 0 until it has been. */
const DRAWER_WIDTH_KEY = 'argus.drawer.width';
/** 28rem: the facts row and the schedule panel still fit side by side. */
const DRAWER_MIN = 448;
const drawerMax = (): number => Math.round(window.innerWidth * 0.96);

function readDrawerWidth(): number {
  try {
    return Number(localStorage.getItem(DRAWER_WIDTH_KEY)) || 0;
  } catch {
    return 0;
  }
}

/** Running rules first, then the ones being written, then history. */
const STATUS_ORDER: Record<string, number> = { deployed: 0, draft: 1, retired: 2 };

/** Worst first, so "severity, descending" puts critical on top. `warning` is an older word for watch. */
const SEVERITY_ORDER: Record<string, number> = { info: 0, watch: 1, warning: 1, critical: 2 };

/** The columns the list sorts by. No column chosen: newest rule first. */
export type RuleSortKey = 'name' | 'scope' | 'severity' | 'owner' | 'changed' | 'status';
export type RuleSort = { key: RuleSortKey; dir: 'asc' | 'desc' } | null;

const text = (a: string, b: string): number => a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });

/**
 * Rules in the order the list shows them. Ties fall back to the newest rule
 * first, then the id, so equal keys never swap places between two reads.
 */
export function sortRules(list: readonly ArgusRule[], sort: RuleSort): ArgusRule[] {
  const created = (a: ArgusRule, b: ArgusRule): number =>
    text(b.created_at, a.created_at) || text(a.id, b.id);
  const by: Record<RuleSortKey, (a: ArgusRule, b: ArgusRule) => number> = {
    name: (a, b) => text(a.name, b.name),
    scope: (a, b) => (a.tree?.entity_count ?? -1) - (b.tree?.entity_count ?? -1),
    severity: (a, b) => (SEVERITY_ORDER[severityOf(a)] ?? 0) - (SEVERITY_ORDER[severityOf(b)] ?? 0),
    owner: (a, b) => text(a.owner_name ?? '', b.owner_name ?? ''),
    changed: (a, b) => text(a.updated_at, b.updated_at),
    // Ascending reads "on first".
    status: (a, b) => (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9),
  };
  if (!sort) return [...list].sort(created);
  const sign = sort.dir === 'asc' ? 1 : -1;
  return [...list].sort((a, b) => sign * by[sort.key](a, b) || created(a, b));
}

/** Both dialects carry a severity at the top of the document. */
function severityOf(rule: ArgusRule): string {
  return (rule.document as { severity?: string } | null)?.severity ?? 'watch';
}

/** "3 hours ago", "yesterday", "12 Sep" - how a list says when. */
/** The drawer's one-word account of who is watching a rule. */
function watchWords(rule: ArgusRule): string {
  const watch = rule.watch;
  if (!watch || watch.state === 'unclaimed') return watch?.reason ?? 'not claimed';
  if (watch.state === 'refused') return 'refused';
  if (watch.state === 'quiet') return `quiet since ${relativeTime(watch.seen_at ?? null)}`;
  return `watching · seen ${relativeTime(watch.seen_at ?? null)}`;
}

function capitalised(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

export function relativeTime(iso: string | null, now: number = Date.now()): string {
  if (!iso) return '-';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '-';
  const minutes = Math.round((now - then) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  return new Date(then).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

/** Focus the element once when it is first drawn - the dialog's first choice. */
class FocusOnce extends Directive {
  private done = false;
  constructor(part: PartInfo) {
    super(part);
    if (part.type !== PartType.ELEMENT) throw new Error('focusOnce goes on an element');
  }
  override update(part: ElementPart): typeof nothing {
    if (!this.done) {
      this.done = true;
      queueMicrotask(() => (part.element as HTMLElement).focus());
    }
    return nothing;
  }
  render(): typeof nothing {
    return nothing;
  }
}
const focusOnce = directive(FocusOnce);

/** A rule written as a tree. The Guided form cannot hold one; the block editor will. */
function isTreeRule(rule: ArgusRule): boolean {
  return (rule.document as { schema_version?: number } | null)?.schema_version === 2;
}

function emptyDocument(sourceKey: string): ArgusRuleDocument {
  return {
    source: sourceKey,
    subject: { entity: '', series: '' },
    shape: 'window',
    aggregate: 'mean',
    window: '24h',
    test: { direction: 'above', value: 0 },
    severity: 'watch',
    gates: [],
  };
}

@customElement('argus-rules-room')
export class ArgusRulesRoom extends LightElement {
  private store = new StoreController(this, [
    rules,
    rulesStatus,
    rulesError,
    sources,
    entities,
    seriesForEntity,
    findings,
    teams,
  ]);
  private refreshTimer: number | undefined;
  /** Where the rail is: a team, a folder, another team's Public rules, or one of mine. */
  @state() private place = readPlace();
  /** The rule being dragged towards a folder on the rail. */
  @state() private dragging: ArgusRule | null = null;

  /** Null when the list is showing; a document when the form is. */
  @state() private draft: ArgusRuleDocument | null = null;
  @state() private draftName = '';
  @state() private editingId: string | null = null;
  /** The right-click menu over a rule row, in viewport coordinates. */
  @state() private rowMenu: { rule: ArgusRule; x: number; y: number } | null = null;
  @state() private formError: string | null = null;
  @state() private saving = false;
  /** The block editor: a rule being changed, `'new'`, or closed. */
  @state() private blockEditing: ArgusRule | 'new' | null = null;
  /** A draft the Assistant handed over, opened in the block editor unsaved. */
  @state() private assistantDraft: AssistantRuleDraft | null = null;
  private unsubscribeDraft: (() => void) | undefined;
  private unsubscribeOpenRule: (() => void) | undefined;
  /** "How do you want to write it?" is open. */
  @state() private choosing = false;
  /** The source a new rule watches: the chooser's first step (S6). */
  @state() private newSource = '';
  /** The rule whose detail is open on the right. */
  @state() private selectedId: string | null = null;
  /** The drawer's width, dragged from its left edge - remembered in this
   *  browser. 0 is the stylesheet's own width, before anyone has dragged. */
  @state() private drawerWidth = readDrawerWidth();

  private setDrawerWidth(px: number): void {
    this.drawerWidth = Math.round(Math.max(DRAWER_MIN, Math.min(px, drawerMax())));
    try {
      localStorage.setItem(DRAWER_WIDTH_KEY, String(this.drawerWidth));
    } catch {
      // A browser that keeps nothing still resizes, for this visit.
    }
  }

  /** The drawer as drawn now, for a keyboard step from wherever it is. */
  private currentDrawerWidth(): number {
    return this.drawerWidth || this.querySelector('.argus-drawer')?.getBoundingClientRect().width || DRAWER_MIN;
  }

  /** The Assistant's grip, on the drawer's left edge: drag it sideways. */
  private onGripDown(e: PointerEvent): void {
    if (e.button !== 0) return;
    const grip = e.currentTarget as HTMLElement;
    e.preventDefault();
    grip.setPointerCapture(e.pointerId);
    document.body.classList.add('resizing-argus-drawer');
    const move = (ev: PointerEvent): void => this.setDrawerWidth(window.innerWidth - ev.clientX);
    const up = (): void => {
      grip.removeEventListener('pointermove', move);
      grip.removeEventListener('pointerup', up);
      grip.removeEventListener('pointercancel', up);
      document.body.classList.remove('resizing-argus-drawer');
    };
    grip.addEventListener('pointermove', move);
    grip.addEventListener('pointerup', up);
    grip.addEventListener('pointercancel', up);
  }

  /** The same resize from the keyboard: a handle only a mouse can use is a
   *  control some people do not have. */
  private onGripKey(e: KeyboardEvent): void {
    const step = e.shiftKey ? 64 : 16;
    if (e.key === 'ArrowLeft') this.setDrawerWidth(this.currentDrawerWidth() + step);
    else if (e.key === 'ArrowRight') this.setDrawerWidth(this.currentDrawerWidth() - step);
    else return;
    e.preventDefault();
  }

  /** The detail's sentence shown in full rather than clamped. */
  @state() private sentenceOpen = false;
  private unsubscribeReselect: (() => void) | undefined;
  @state() private query = '';
  @state() private stateFilter: StateFilter = 'all';
  @state() private onlyMine = false;
  @state() private severity: string | null = null;
  @state() private found: FoundFilter | null = null;
  @state() private owner: string | null = null;
  @state() private needsBacktest = false;
  @state() private sort: RuleSort = null;
  @state() private page = 0;
  @state() private pageSize = readPageSize();
  /**
   * The order the list was last drawn in, and what it was drawn for. Kept
   * while only the rules' own fields change - deploying one bumps its status
   * and its "changed" time, and the row must not jump away from the pointer
   * that just flipped it. A new sort, filter or search, or a rule added or
   * removed, orders the list afresh.
   */
  private drawnOrder: { key: string; ids: string[] } | null = null;

  private mayAuthor(): boolean {
    return canDo('argus:author');
  }

  private entityName(id: string): string {
    return entities.value.find((e) => e.id === id)?.name ?? id;
  }

  private entityNoun(): string {
    const source =
      sources.value.find((s) => s.key === (this.draft?.source ?? '')) ?? sources.value[0];
    return source?.entity_noun ?? 'entity';
  }

  /** "ship" -> "ships", "entity" -> "entities". The noun is the source's. */
  private entityNouns(): string {
    const noun = this.entityNoun();
    return /[^aeiou]y$/.test(noun) ? `${noun.slice(0, -1)}ies` : `${noun}s`;
  }

  /* ── the rail's place ──────────────────────────────────── */

  /** The place as remembered, or the first team on the rail when it is gone. */
  private currentPlace(): string {
    const [kind, team, folder] = this.place.split(':');
    const rail = teams.value.teams;
    const valid =
      kind === 'followed' || kind === 'mine' || kind === 'drafts' ||
      (kind === 'team' && rail.some((t) => t.id === team)) ||
      (kind === 'folder' && rail.some((t) => t.id === team && t.folders.some((f) => f.id === folder))) ||
      (kind === 'public' && rules.value.some((r) => r.group_id === team && !r.in_team));
    if (valid) return this.place;
    return rail[0] ? `team:${rail[0].id}` : '';
  }

  private setPlace(place: string): void {
    this.refilter(() => (this.place = place));
    try {
      localStorage.setItem(PLACE_KEY, place);
    } catch {
      /* a remembered place is a convenience */
    }
  }

  private inPlace(rule: ArgusRule): boolean {
    const [kind, team, folder] = this.currentPlace().split(':');
    switch (kind) {
      case 'team':
      case 'public':
        return rule.group_id === team;
      case 'folder':
        return rule.folder_id === folder;
      case 'followed':
        return !!rule.followed;
      case 'mine':
        return rule.owner_id === myUserId.value;
      case 'drafts':
        return rule.owner_id === myUserId.value && rule.status === 'draft';
      default:
        return true;
    }
  }

  private renderNoTeam(): TemplateResult | typeof nothing {
    if (!inNoTeam()) return nothing;
    return html`<div class="argus-no-team" role="status">
      <strong>Join a team to work in Argus.</strong>
      <p>
        Rules and their alerts belong to teams. You write rules, handle alerts and deploy only in a
        team you are in, whatever your Argus role. Ask an administrator to add you to a team.
      </p>
      <p>Until then you can read and follow Public rules from other teams.</p>
    </div>`;
  }

  /** May this person file the rule into a folder - their team's, or any, for an administrator? */
  private mayFile(rule: ArgusRule): boolean {
    return this.mayAuthor() && (!!rule.in_team || teams.value.sees_all);
  }

  /** The head of the list: what this place is, who shares it, and what can be done here. */
  private renderPlaceHead(): TemplateResult {
    const [kind, teamId, folderId] = this.currentPlace().split(':');
    const team = teams.value.teams.find((t) => t.id === teamId);
    const here = rules.value.filter((r) => this.inPlace(r));
    const write = this.mayAuthor() && !inNoTeam()
      ? html`<button type="button" class="btn btn-primary" @click=${() => this.startWriting()}>
          ${kind === 'folder' ? 'Write a rule here' : 'Write a rule'}
        </button>`
      : nothing;
    const badge = (id: string, name: string): TemplateResult =>
      html`<span class="argus-rail-badge argus-ph-badge" data-hue=${teamHue(id)}>${teamInitials(name)}</span>`;
    const n = (count: number): string => `${count} rule${count === 1 ? '' : 's'}`;
    let icon: TemplateResult = html`<span class="argus-ph-icon"></span>`;
    let crumbs: TemplateResult | string = 'Rules';
    let title = 'Rules';
    let sub = n(here.length);
    if (kind === 'team' && team) {
      icon = badge(team.id, team.name);
      crumbs = 'Team';
      title = team.name;
      sub = `${n(here.length)} · ${team.folders.length} folder${team.folders.length === 1 ? '' : 's'} · shared by ${team.people} ${team.people === 1 ? 'person' : 'people'}`;
    } else if (kind === 'folder' && team) {
      const folder = team.folders.find((f) => f.id === folderId);
      icon = html`<span class="argus-ph-icon is-folder"></span>`;
      crumbs = html`<button type="button" class="argus-ph-crumb" @click=${() => this.setPlace(`team:${team.id}`)}>${team.name}</button> › Folder`;
      title = folder?.name ?? 'Folder';
      sub = `${n(here.length)} · everyone in ${team.name} sees this folder`;
    } else if (kind === 'public') {
      const name = rules.value.find((r) => r.group_id === teamId)?.group_name ?? 'Another team';
      icon = badge(teamId ?? '', name);
      crumbs = 'Public from another team';
      title = name;
      sub = `Read-only. ${name} judges and closes these alerts. Follow a rule to get its alerts in your Inbox.`;
    } else if (kind === 'followed') {
      icon = html`<span class="argus-ph-icon is-bell"></span>`;
      crumbs = 'For me';
      title = 'Followed';
      sub = 'Their alerts come to your Inbox marked Followed, and count in the dimmed number beside ARGUS. You read them and add notes.';
    } else if (kind === 'mine' || kind === 'drafts') {
      icon = html`<span class="argus-ph-icon is-user"></span>`;
      crumbs = 'For me';
      title = kind === 'mine' ? 'Made by me' : 'My drafts';
      sub = kind === 'mine'
        ? 'Rules you wrote, in every team you see.'
        : 'Not deployed yet. A draft needs a backtest of its current version before a leader deploys it.';
    }
    return html`<div class="argus-ph">
      ${icon}
      <div class="argus-ph-text">
        <div class="argus-ph-crumbs">${crumbs}</div>
        <h2>${title}</h2>
        <div class="argus-ph-sub">${sub}</div>
      </div>
      <div class="argus-ph-acts">${write}</div>
    </div>`;
  }

  /** "Write a rule" from a place: the new rule goes into that team, and that folder. */
  private startWriting(): void {
    const [kind, team, folder] = this.currentPlace().split(':');
    const mine = teams.value.teams.filter((t) => t.in_team || teams.value.sees_all);
    const teamId = (kind === 'team' || kind === 'folder') && team ? team : mine[0]?.id;
    newRulePlace.set(teamId ? { teamId, folderId: kind === 'folder' ? folder ?? null : null } : null);
    this.newSource = sources.value[0]?.key ?? 'historical';
    this.choosing = true;
  }

  private async fileRule(rule: ArgusRule, folderId: string | null): Promise<void> {
    const failed = await moveRuleToFolder(rule.id, folderId);
    if (failed) {
      showToast('error', failed);
      return;
    }
    const team = teams.value.teams.find((t) => t.id === rule.group_id);
    const folder = team?.folders.find((f) => f.id === folderId);
    showToast('success', folder ? `${rule.name} is in ${folder.name}.` : `${rule.name} is at the top of ${team?.name ?? 'its team'}.`);
  }

  private async changeVisibility(rule: ArgusRule, visibility: 'team' | 'public'): Promise<void> {
    const failed = await setVisibility(rule.id, visibility);
    if (failed) showToast('error', failed);
    else
      showToast(
        'success',
        visibility === 'public'
          ? `Everyone with Argus can read ${rule.name} now. Its alerts stay with ${rule.group_name}.`
          : `Only ${rule.group_name} sees ${rule.name} now.`,
      );
  }

  private async changeFollow(rule: ArgusRule, follow: boolean): Promise<void> {
    const failed = await setFollow(rule.id, follow);
    if (failed) showToast('error', failed);
    else showToast('success', follow ? `Following ${rule.name}. Its alerts come to your Inbox, marked Followed.` : `Unfollowed ${rule.name}.`);
  }

  /* ── the list ─────────────────────────────────────────── */

  private visible(rule: ArgusRule): boolean {
    if (!this.inPlace(rule)) return false;
    if (this.stateFilter !== 'all' && rule.status !== this.stateFilter) return false;
    if (this.onlyMine && rule.owner_id !== myUserId.value) return false;
    if (this.owner && rule.owner_id !== this.owner) return false;
    if (this.severity && severityOf(rule) !== this.severity) return false;
    if (this.found && foundOf(rule) !== this.found) return false;
    if (this.needsBacktest && rule.backtested !== false) return false;
    const q = this.query.trim().toLowerCase();
    if (!q) return true;
    const haystack = [
      rule.name,
      rule.description ?? '',
      rule.sentence ?? '',
      rule.owner_name,
      rule.group_name ?? '',
      rule.tree?.scope_words ?? '',
    ]
      .join(' ')
      .toLowerCase();
    return haystack.includes(q);
  }

  /** Change a filter and go back to the first page, where its results start. */
  private refilter(change: () => void): void {
    change();
    this.page = 0;
  }

  private filtered(): boolean {
    return (
      this.stateFilter !== 'all' || this.onlyMine || !!this.owner || !!this.severity || !!this.found ||
      this.needsBacktest || !!this.query.trim()
    );
  }

  private clearFilters(): void {
    this.refilter(() => {
      this.stateFilter = 'all';
      this.onlyMine = false;
      this.owner = null;
      this.severity = null;
      this.found = null;
      this.needsBacktest = false;
      this.query = '';
    });
  }

  private chip(label: string, on: boolean, toggle: () => void, count?: number): TemplateResult {
    return html`<button
      type="button"
      class="argus-fchip ${on ? 'is-on' : ''}"
      aria-pressed=${on ? 'true' : 'false'}
      @click=${() => this.refilter(toggle)}
    >
      ${label}${count != null ? html`<b>${count}</b>` : nothing}
    </button>`;
  }

  private renderToolbar(): TemplateResult {
    const all = rules.value.filter((r) => this.inPlace(r));
    const count = (st: string): number => all.filter((r) => r.status === st).length;
    const state = (filter: StateFilter, label: string, n: number): TemplateResult =>
      this.chip(label, this.stateFilter === filter, () => {
        this.stateFilter = this.stateFilter === filter && filter !== 'all' ? 'all' : filter;
      }, n);
    return html`
      <div class="argus-toolbar">
        <input
          class="argus-search"
          type="search"
          placeholder="Find a rule, a signal, a ship, an owner…"
          aria-label="Find a rule"
          .value=${this.query}
          @input=${(e: Event) => this.refilter(() => (this.query = (e.target as HTMLInputElement).value))}
        />
        ${state('all', 'All', all.length)} ${state('deployed', 'Deployed', count('deployed'))}
        ${state('draft', 'Drafts', count('draft'))}
        ${count('retired') ? state('retired', 'Retired', count('retired')) : nothing}
      </div>
      ${this.renderFineFilters(all)}
    `;
  }

  /**
   * The second row: severity, what the watcher found, who owns it, and what
   * still needs a backtest. Counts are over every rule, so a chip says how
   * many there are, not how many survive the other filters.
   */
  private renderFineFilters(all: readonly ArgusRule[]): TemplateResult {
    const bySeverity = (sev: string): number => all.filter((r) => severityOf(r) === sev).length;
    const byFound = (f: FoundFilter): number => all.filter((r) => foundOf(r) === f).length;
    const severity = (sev: string, label: string): TemplateResult | typeof nothing =>
      bySeverity(sev)
        ? this.chip(label, this.severity === sev, () => (this.severity = this.severity === sev ? null : sev), bySeverity(sev))
        : nothing;
    const found = (f: FoundFilter, label: string, title: string): TemplateResult | typeof nothing =>
      byFound(f)
        ? html`<span title=${title}>${this.chip(label, this.found === f, () => (this.found = this.found === f ? null : f), byFound(f))}</span>`
        : nothing;
    const owners = [...new Map(all.map((r) => [r.owner_id, r.owner_name] as const)).entries()].sort((a, b) =>
      text(a[1] ?? '', b[1] ?? ''),
    );
    const unbacktested = all.filter((r) => r.backtested === false).length;
    return html`<div class="argus-toolbar argus-toolbar-fine">
      <span class="argus-toolbar-label">Severity</span>
      ${severity('critical', 'Critical')} ${severity('watch', 'Watch')} ${severity('info', 'Info')}
      <span class="argus-toolbar-label">Found</span>
      ${found('alerting', 'Alert active', 'Running, with something open now')}
      ${found('quiet', 'Nothing open', 'Running and watched, nothing open')}
      ${found('trouble', 'Not watched', 'Running, but no watcher is evaluating it: unclaimed, refused, or its watcher has gone quiet')}
      ${unbacktested
        ? this.chip('Needs a backtest', this.needsBacktest, () => (this.needsBacktest = !this.needsBacktest), unbacktested)
        : nothing}
      ${owners.length > 1
        ? html`<select
            class="argus-fselect ${this.owner ? 'is-on' : ''}"
            aria-label="Owner"
            @change=${(e: Event) => this.refilter(() => (this.owner = (e.target as HTMLSelectElement).value || null))}
          >
            <option value="" ?selected=${!this.owner}>Any owner</option>
            ${owners.map(([id, name]) => html`<option value=${id} ?selected=${this.owner === id}>${name}</option>`)}
          </select>`
        : nothing}
      ${this.filtered()
        ? html`<button type="button" class="argus-lmore argus-clear-filters" @click=${() => this.clearFilters()}>
            Clear filters
          </button>`
        : nothing}
    </div>`;
  }

  private renderRow(rule: ArgusRule): TemplateResult {
    const severity = severityOf(rule);
    const count = rule.tree?.entity_count;
    const noun = rule.tree?.entity_noun ?? 'entity';
    const scope = rule.tree?.scope_words ?? '-';
    return html`
      <tr
        class="argus-lrow is-sev-${severity} is-${rule.status} ${this.selectedId === rule.id ? 'is-selected' : ''} ${this.dragging?.id === rule.id ? 'is-dragging' : ''}"
        tabindex="0"
        aria-selected=${this.selectedId === rule.id ? 'true' : 'false'}
        draggable=${this.mayFile(rule) ? 'true' : 'false'}
        @dragstart=${(e: DragEvent) => {
          if (!this.mayFile(rule)) return;
          e.dataTransfer?.setData('text/plain', rule.name);
          if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
          this.setDragTag(e, rule, severity);
          // A frame later: changing what is drawn inside dragstart aborts the drag.
          requestAnimationFrame(() => (this.dragging = rule));
        }}
        @dragend=${() => (this.dragging = null)}
        @click=${() => this.select(rule)}
        @contextmenu=${(e: MouseEvent) => {
          e.preventDefault();
          this.rowMenu = { rule, x: e.clientX, y: e.clientY };
        }}
        @keydown=${(e: KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.select(rule);
          }
        }}
      >
        <td>
          <div class="argus-lname">
            <span class="argus-ltext">${rule.name}</span>
            ${rule.source_key === 'live'
              ? html`<span class="argus-chip is-live" title="Watches Live data: raw, the last 48 hours.">live</span>`
              : nothing}
            ${rule.visibility === 'public' && rule.may_handle !== false
              ? html`<span class="argus-chip is-public" title="Everyone with Argus can read this rule. Only ${rule.group_name} acts on it.">public</span>`
              : nothing}
            ${rule.status === 'deployed'
              ? nothing
              : html`<span class="argus-chip is-${rule.status}">${rule.status}</span>`}
          </div>
          <div class="argus-lsentence argus-ltext" title=${rule.sentence ?? ''}>
            ${rule.sentence ?? 'This rule has no version yet.'}
          </div>
          ${rule.folder_name && !this.currentPlace().startsWith('folder:')
            ? html`<div class="argus-lfolder argus-ltext" title=${`In the folder ${rule.folder_name}`}>${rule.folder_name}</div>`
            : nothing}
        </td>
        <td>
          <div class="argus-ltext" title=${scope}>${scope}</div>
          <div class="argus-lfaint argus-ltext">
            ${count != null ? `${count} ${noun}${count === 1 ? '' : 's'}` : ' '}
          </div>
        </td>
        <td>${this.renderFound(rule)}</td>
        <td><span class="argus-chip is-${severity}">${severity}</span></td>
        <td><div class="argus-ltext" title=${rule.owner_name}>${rule.owner_name}</div></td>
        <td class="argus-lfaint"><div class="argus-ltext">${relativeTime(rule.updated_at)}</div></td>
        <td class="argus-lswitch" @click=${(e: Event) => e.stopPropagation()}>
          ${rule.may_handle === false
            ? this.followButton(rule)
            : html`<hold-switch
                .checked=${rule.status === 'deployed'}
                label=${`Deployed: ${rule.name}`}
                hint-on=${rule.backtested === false ? `Backtest version ${rule.version} first` : 'Hold to deploy'}
                hint-off="Hold to stop"
                @hold-commit=${(e: CustomEvent<{ checked: boolean }>) =>
                  void this.changeStatus(rule, e.detail.checked ? 'deployed' : 'draft')}
              ></hold-switch>`}
        </td>
      </tr>
    `;
  }

  /** The pointer carries a small tag - severity and name - not the whole row. */
  private setDragTag(e: DragEvent, rule: ArgusRule, severity: string): void {
    const tag = this.querySelector<HTMLElement>('.argus-drag-tag');
    if (!tag || !e.dataTransfer) return;
    tag.className = `argus-drag-tag is-sev-${severity}`;
    tag.querySelector('.argus-drag-name')!.textContent = rule.name;
    e.dataTransfer.setDragImage(tag, 14, 14);
  }

  /** Another team's rule: Follow in place of the switch. Shown on hover until followed. */
  private followButton(rule: ArgusRule): TemplateResult {
    const on = !!rule.followed;
    return html`<button
      type="button"
      class="argus-follow ${on ? 'is-on' : ''}"
      aria-pressed=${on ? 'true' : 'false'}
      title=${on ? 'Its alerts come to your Inbox. Click to stop.' : `Get its alerts in your Inbox. ${rule.group_name} still handles them.`}
      @click=${() => void this.changeFollow(rule, !on)}
    >${on ? 'Following' : 'Follow'}</button>`;
  }

  /**
   * The watcher's word on a rule, in one cell. Quiet when there is nothing to
   * say (rule 10): a draft shows a dash, a clean run shows "nothing found".
   * An episode still going on is the one thing drawn in a colour.
   */
  private renderFound(rule: ArgusRule): TemplateResult {
    const watch = rule.watch ?? { state: 'unclaimed' as const };
    if (rule.status !== 'deployed') return html`<span class="argus-lfaint">-</span>`;
    if (watch.state === 'unclaimed') {
      return html`<div class="argus-ltext argus-lfaint" title=${watch.reason ?? 'No watcher has claimed this rule'}>not claimed</div>`;
    }
    if (watch.state === 'refused') {
      return html`<div class="argus-ltext argus-found-refused" title=${watch.reason ?? ''}>refused</div>
        <div class="argus-lfaint argus-ltext" title=${watch.reason ?? ''}>${watch.reason ?? ''}</div>`;
    }
    const episodes = watch.episodes ?? 0;
    const open = watch.needs_look ?? 0;
    const head = !watch.entities
      ? html`<span class="argus-lfaint">claimed, not run yet</span>`
      : episodes
        ? open
          ? this.openLink(rule, open)
          : html`nothing open`
        : html`nothing found`;
    // The live dot's ping reaches past its box; a clipped cell would cut it.
    return html`<div class="argus-ltext ${open ? 'argus-found-live' : ''}">${head}</div>
      <div class="argus-lfaint argus-ltext ${watch.state === 'quiet' ? 'argus-found-quiet' : ''}">
        ${watch.state === 'quiet' ? 'watcher quiet · ' : ''}${watch.checked_at ? `checked ${relativeTime(watch.checked_at)}` : ''}
      </div>`;
  }

  /** Open this rule's episodes in the Inbox (decision 24). */
  private toInbox(rule: ArgusRule): void {
    inboxRuleFilter.set({ id: rule.id, name: rule.name });
    setArgusRoom('inbox');
  }

  /**
   * "Alert active" in the Found cell, as a way into the Inbox. Whether
   * something is going on, not how much: the count is the Inbox's to give.
   */
  private openLink(rule: ArgusRule, open: number): TemplateResult {
    return html`<button
      type="button"
      class="argus-found-link glow-alive is-${severityOf(rule)}"
      title=${`${open} open - see them in the Inbox`}
      @click=${(e: Event) => {
        e.stopPropagation();
        this.toInbox(rule);
      }}
    >
      <span class="glow-dot" aria-hidden="true"></span><span class="argus-found-link-n">Alert active</span>
    </button>`;
  }

  /**
   * The drawer's one line about what needs attention: a glowing chip when
   * something is open, nothing at all when nothing is (rule 10). The episodes
   * themselves are in the Inbox, not here (decision 24).
   */
  private needsLook(rule: ArgusRule): TemplateResult | typeof nothing {
    const open = rule.watch?.needs_look ?? 0;
    if (!open) return nothing;
    return html`<p class="argus-found-note">
      <button
        type="button"
        class="glow-chip glow-alive argus-needs-look is-${severityOf(rule)}"
        title=${`${open} open - see them in the Inbox`}
        @click=${() => this.toInbox(rule)}
      >
        <span class="glow-dot" aria-hidden="true"></span>Alert active · review
      </button>
    </p>`;
  }

  /**
   * The drawer's middle zone: what the watcher found, entity by entity. Only a
   * deployed rule has one - a draft is not watched, and says so above it.
   */
  private renderFindings(rule: ArgusRule): TemplateResult | typeof nothing {
    if (rule.status !== 'deployed') return nothing;
    return html`<section class="argus-dz" aria-label="What it found">${this.findingsBody(rule)}</section>`;
  }

  private findingsBody(rule: ArgusRule): TemplateResult {
    const found = findings.value[rule.id];
    const head = (span = ''): TemplateResult =>
      html`<h4 class="argus-dz-h">What it found <span class="argus-lfaint">${span}</span></h4>`;
    if (!found || found === 'loading') return html`${head()}<p class="argus-lfaint">Asking what the watcher found…</p>`;
    if (found === 'failed') return html`${head()}<p class="argus-form-error">What the watcher found could not be read.</p>`;
    if (!found.evaluations.length) {
      return html`${head()}<p class="argus-lfaint">
        ${rule.watch?.state === 'refused' ? `Not run: ${rule.watch.reason}.` : 'No watcher has run this version yet.'}
      </p>`;
    }
    const byEntity = new Map<string, number>();
    for (const e of found.episodes) byEntity.set(e.entity, (byEntity.get(e.entity) ?? 0) + 1);
    const first = found.evaluations[0];
    const span = first ? `${new Date(first.range_from).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })} – ${new Date(first.range_to).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}` : '';
    return html`
      ${head(span)}
      ${this.needsLook(rule)}
      <table class="argus-found-table">
        <thead><tr><th>${capitalised(rule.tree?.entity_noun ?? 'entity')}</th><th title="Steps where the gate held and the data was there">Counted</th><th title="Share of counted steps where the condition held">Held</th><th>Episodes</th></tr></thead>
        <tbody>
          ${found.evaluations.map(
            (e) => html`<tr class=${e.counted ? '' : 'is-dim'} title=${e.note ?? ''}>
              <td>${this.entityName(e.entity)}</td>
              <td>${e.counted.toLocaleString()}</td>
              <td>${e.counted ? `${Math.round((100 * e.true_steps) / e.counted)}%` : '-'}</td>
              <td>${byEntity.get(e.entity) ?? 0}</td>
            </tr>`,
          )}
        </tbody>
      </table>
    `;
  }

  private select(rule: ArgusRule): void {
    this.selectedId = this.selectedId === rule.id ? null : rule.id;
    this.sentenceOpen = false;
    if (this.selectedId) {
      void loadFindings(rule.id);
      if (!entities.value.length) void loadEntities(rule.source_key);
    }
  }

  /** The drawer starts where the room starts, below the app's header rows. */
  private placeDrawer = (): void => {
    const host = this.closest('argus-section') ?? this;
    const top = Math.max(0, Math.round(host.getBoundingClientRect().top));
    this.style.setProperty('--argus-drawer-top', `${top}px`);
  };

  private onKey = (e: KeyboardEvent): void => {
    if (e.key !== 'Escape') return;
    if (this.rowMenu) this.rowMenu = null;
    else if (this.selectedId && !this.choosing) this.selectedId = null;
  };

  /**
   * The row's right-click menu: straight to the editor, or the drawer. Clamped
   * so a menu opened on the last row does not open off-screen.
   */
  private renderRowMenu(): TemplateResult | typeof nothing {
    const at = this.rowMenu;
    if (!at) return nothing;
    const { rule } = at;
    const x = Math.min(at.x, window.innerWidth - 240);
    const y = Math.max(8, Math.min(at.y, window.innerHeight - 280));
    const entry = (icon: string, label: string, run: () => void, title = ''): TemplateResult =>
      html`<button class="cat-menu-item" role="menuitem" type="button" title=${title} @click=${() => {
        this.rowMenu = null;
        run();
      }}>
        <span class="cat-menu-icon" aria-hidden="true">${icon}</span> ${label}
      </button>`;
    const editable = this.mayAuthor() && (rule.tree || !isTreeRule(rule));
    return html`<div
      class="cat-menu argus-ibx-menu"
      role="menu"
      style=${`left:${x}px; top:${y}px`}
      @contextmenu=${(e: Event) => e.preventDefault()}
    >
      ${editable
        ? entry('✎', 'Edit', () => {
            this.selectedId = null;
            this.openEditor(rule);
          }, rule.status === 'deployed'
            ? 'It is running: the name and description can be changed at once; a change to the rule itself waits until it is stopped.'
            : '')
        : nothing}
      ${entry('▸', this.selectedId === rule.id ? 'Hide details' : 'Show details', () => this.select(rule))}
      ${this.mayFile(rule) ? this.moveEntries(rule, entry) : nothing}
      ${rule.can_lead
        ? html`<hr class="cat-menu-sep" />${rule.visibility === 'public'
            ? entry('◐', 'Make Team only', () => void this.changeVisibility(rule, 'team'),
                `Only ${rule.group_name} will see it. Follows from outside the team end.`)
            : entry('◍', 'Make Public', () => void this.changeVisibility(rule, 'public'),
                `Everyone with Argus can read it. Its alerts stay with ${rule.group_name}.`)}`
        : nothing}
      ${!rule.in_team
        ? html`<hr class="cat-menu-sep" />${entry(rule.followed ? '○' : '●', rule.followed ? 'Unfollow' : 'Follow',
            () => void this.changeFollow(rule, !rule.followed),
            rule.followed ? '' : `Its alerts come to your Inbox. ${rule.group_name} still handles them.`)}`
        : nothing}
    </div>`;
  }

  /** Move to each other folder of the rule's team, and out of its own. */
  private moveEntries(
    rule: ArgusRule,
    entry: (icon: string, label: string, run: () => void, title?: string) => TemplateResult,
  ): TemplateResult | typeof nothing {
    const team = teams.value.teams.find((t) => t.id === rule.group_id);
    const others = (team?.folders ?? []).filter((f) => f.id !== rule.folder_id);
    if (!others.length && !rule.folder_id) return nothing;
    return html`<hr class="cat-menu-sep" />
      ${others.map((f) => entry('▭', `Move to ${f.name}`, () => void this.fileRule(rule, f.id)))}
      ${rule.folder_id ? entry('↑', 'Take out of the folder', () => void this.fileRule(rule, null)) : nothing}`;
  }

  /**
   * A click anywhere but the drawer, a rule row, or something the drawer opened
   * (a dialog, a switch's hint) puts the drawer away. A row click is the row's
   * own business: another rule swaps the drawer, the same rule closes it.
   */
  private onOutside = (e: Event): void => {
    const target = e.target as Element | null;
    if (!target) return;
    if (this.rowMenu && !target.closest('.cat-menu')) this.rowMenu = null;
    if (!this.selectedId) return;
    if (target.closest('.argus-drawer, .argus-drawer-grip, tr.argus-lrow, [role="dialog"], .hs-hint, .cat-menu')) return;
    this.selectedId = null;
  };

  /** Clicking Rules while already in Rules: back to the list, from anywhere. */
  private backToList = (): void => {
    this.blockEditing = null;
    this.assistantDraft = null;
    this.choosing = false;
    this.selectedId = null;
    this.close();
  };

  override connectedCallback(): void {
    super.connectedCallback();
    void loadRules();
    void loadTeams();
    void loadSources();
    void loadWatchers();
    // What the watcher says changes on its own clock; look again each minute
    // while the room is open, so "checked 3 min ago" does not quietly lie.
    this.refreshTimer = window.setInterval(() => {
      void loadRules();
      void loadWatchers();
      if (this.selectedId) void loadFindings(this.selectedId);
    }, 60_000);
    window.addEventListener('resize', this.placeDrawer);
    document.addEventListener('keydown', this.onKey);
    // Both: a pointer press, and a bare mouse press where no pointer event comes.
    document.addEventListener('pointerdown', this.onOutside, true);
    document.addEventListener('mousedown', this.onOutside, true);
    this.unsubscribeReselect = argusRoomReselected.subscribe(this.backToList);
    this.unsubscribeDraft = assistantDraft.subscribe(() => void this.takeAssistantDraft());
    void this.takeAssistantDraft();
    // Asked while the room is already open, too: an Assistant card for a rule
    // it just saved.
    this.unsubscribeOpenRule = rulesRoomOpenRule.subscribe(() => void this.takeOpenRule());
    void this.takeOpenRule();
  }

  /**
   * A rule the Inbox or an Assistant card asked to show: open it in the drawer
   * once the list is in. While an editor is open the request waits for it to
   * close - opening a rule never throws away somebody's unsaved work.
   */
  private async takeOpenRule(): Promise<void> {
    const id = rulesRoomOpenRule.value;
    if (!id || this.draft || this.blockEditing || this.choosing) return;
    rulesRoomOpenRule.set(null);
    if (!rules.value.some((r) => r.id === id)) await loadRules();
    const rule = rules.value.find((r) => r.id === id);
    if (rule && this.selectedId !== id) this.select(rule);
  }

  /**
   * Open the Assistant's draft in the block editor, once. A change to an
   * existing rule waits for the list, so the editor knows which rule it is
   * changing and saving writes that rule's next version.
   */
  private async takeAssistantDraft(): Promise<void> {
    const draft = assistantDraft.value;
    if (!draft) return;
    assistantDraft.set(null);
    // A new rule from someone in no team cannot be saved: the notice says why, so no editor.
    if (!draft.ruleId) {
      if (!teamsLoaded.value) await loadTeams();
      if (inNoTeam()) return;
    }
    let rule: ArgusRule | null = null;
    if (draft.ruleId) {
      if (!rules.value.some((r) => r.id === draft.ruleId)) await loadRules();
      rule = rules.value.find((r) => r.id === draft.ruleId) ?? null;
    }
    this.draft = null;
    this.choosing = false;
    this.selectedId = null;
    this.assistantDraft = draft;
    this.blockEditing = rule ?? 'new';
  }

  override disconnectedCallback(): void {
    window.removeEventListener('resize', this.placeDrawer);
    document.removeEventListener('keydown', this.onKey);
    document.removeEventListener('pointerdown', this.onOutside, true);
    document.removeEventListener('mousedown', this.onOutside, true);
    this.unsubscribeReselect?.();
    this.unsubscribeDraft?.();
    this.unsubscribeOpenRule?.();
    window.clearInterval(this.refreshTimer);
    super.disconnectedCallback();
  }

  protected override updated(): void {
    if (this.selectedId) this.placeDrawer();
    if (this.drawerWidth) this.style.setProperty('--argus-drawer-w', `${this.drawerWidth}px`);
  }

  /**
   * The strip under the drawer's head: on or off, the row's own switch, and
   * whether anybody is watching. Rule 9 - a rule nobody evaluates must not
   * read as one that runs - is why the watcher's words sit beside the switch.
   */
  private renderState(rule: ArgusRule): TemplateResult {
    const deployed = rule.status === 'deployed';
    return html`<div class="argus-dz-state ${deployed ? 'is-on' : ''}">
      <!-- The row's own switch, so the drawer and the row read and act alike.
           Offered to everyone; the database refuses who it must. -->
      <span class="argus-lswitch">${rule.may_handle === false
        ? this.followButton(rule)
        : html`<hold-switch
            .checked=${deployed}
            label=${`Deployed: ${rule.name}`}
            hint-on=${rule.backtested === false ? `Backtest version ${rule.version} first` : 'Hold to deploy'}
            hint-off="Hold to stop"
            @hold-commit=${(e: CustomEvent<{ checked: boolean }>) =>
              void this.changeStatus(rule, e.detail.checked ? 'deployed' : 'draft')}
          ></hold-switch>`}</span>
      <p>
        ${deployed
          ? html`<b>Deployed</b>${rule.deployed_at ? ` ${relativeTime(rule.deployed_at)}` : ''} ·
              <span class=${rule.watch?.state === 'watching' ? '' : 'is-dim'} title=${rule.watch?.reason ?? ''}
                >${watchWords(rule)}</span
              >
              <br /><span class="argus-lfaint"
                >Watches ${rule.tree?.entity_count != null ? plural(rule.tree.entity_count, rule.tree.entity_noun ?? 'entity') : '-'}</span
              >`
          : html`<b>Draft</b> · not running
              <br /><span class="argus-lfaint"
                >${rule.backtested === false
                  ? `Backtest version ${rule.version}, then hold the switch to deploy.`
                  : 'Hold the switch to deploy.'}</span
              >`}
      </p>
    </div>`;
  }

  private renderDetail(rule: ArgusRule): TemplateResult {
    const severity = severityOf(rule);
    const author = this.mayAuthor();
    return html`
      <!-- Outside the aside: the aside scrolls, and a grip inside it would
           scroll away with the content and be clipped at the edge. -->
      <div
        class="argus-drawer-grip"
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize the rule panel"
        aria-valuenow=${Math.round(this.currentDrawerWidth())}
        aria-valuemin=${DRAWER_MIN}
        aria-valuemax=${drawerMax()}
        tabindex="0"
        title="Drag to resize"
        @pointerdown=${(e: PointerEvent) => this.onGripDown(e)}
        @keydown=${(e: KeyboardEvent) => this.onGripKey(e)}
      >
        <span class="argus-drawer-grip-line" aria-hidden="true"></span>
      </div>
      <aside class="argus-drawer" aria-label=${`Rule: ${rule.name}`}>
        <!-- Three zones under a head and a status strip (sketch
             docs/design/sketches/argus-rule-drawer.html, option A): what the
             rule says, what it found, how it runs. -->
        <header class="argus-dz-head">
          <div class="argus-dz-title">
            <h3>${rule.name}</h3>
            <span class="argus-drawer-tools">
              ${author
                ? html`<button
                    type="button"
                    class="argus-drawer-tool"
                    aria-label=${rule.status === 'deployed' ? 'Open in the editor' : 'Edit'}
                    title=${rule.status === 'deployed'
                      ? 'Open in the editor. It is running: a new name or description saves at once; a change to the rule itself can be checked and backtested there, and saved once it is stopped.'
                      : 'Edit. A change is saved as the next version.'}
                    @click=${() => this.openEditor(rule)}
                  >
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                      <path
                        d="M10.6 2.6l2.8 2.8M2.5 13.5l.7-3.3 7.9-7.9a1.2 1.2 0 0 1 1.7 0l1 1a1.2 1.2 0 0 1 0 1.7l-7.9 7.9-3.4.6z"
                        stroke="currentColor"
                        stroke-width="1.3"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </button>`
                : nothing}
              <button
                type="button"
                class="argus-drawer-tool argus-drawer-close"
                aria-label="Close"
                title="Close"
                @click=${() => (this.selectedId = null)}
              >
                ×
              </button>
            </span>
          </div>
          <p class="argus-dz-meta">
            <span class="argus-chip is-${severity}">${severity}</span>
            <span>
              v${rule.version} · ${rule.group_name ?? 'no team yet'} · ${rule.owner_name} · changed
              ${relativeTime(rule.updated_at)}
            </span>
          </p>
        </header>
        ${this.renderState(rule)}
        <section class="argus-dz" aria-label="What it says">
          <h4 class="argus-dz-h">What it says</h4>
          ${rule.description ? html`<p class="argus-ldetail-about">${rule.description}</p>` : nothing}
          <p class="argus-ldetail-sentence ${this.sentenceOpen ? 'is-open' : ''}">
            ${rule.sentence ?? 'This rule has no version yet.'}
          </p>
          ${(rule.sentence ?? '').length > 180
            ? html`<button
                type="button"
                class="argus-lmore"
                aria-expanded=${this.sentenceOpen ? 'true' : 'false'}
                @click=${() => {
                  this.sentenceOpen = !this.sentenceOpen;
                }}
              >
                ${this.sentenceOpen ? 'Show less' : 'Show all'}
              </button>`
            : nothing}
          <details class="argus-dz-fold">
            <summary>Blocks</summary>
            ${rule.tree
              ? html`<argus-rule-blocks .tree=${rule.tree}></argus-rule-blocks>`
              : html`<p class="argus-room-waiting">This rule cannot be drawn as blocks.</p>`}
          </details>
        </section>
        ${this.renderFindings(rule)}
        <section class="argus-dz" aria-label="How it runs">
          <h4 class="argus-dz-h">How it runs</h4>
          <argus-schedule-panel .rule=${rule} ?editable=${author} summary></argus-schedule-panel>
          <argus-backtest-panel
            .ruleId=${rule.id}
            .ruleVersion=${rule.version}
            .ruleName=${rule.name}
            .noun=${rule.tree?.entity_noun ?? 'entity'}
          ></argus-backtest-panel>
        </section>
        ${rulesError.value ? html`<p class="argus-form-error argus-dz-error" role="alert">${rulesError.value}</p>` : nothing}
      </aside>
    `;
  }

  private renderList(): TemplateResult {
    // `idle` is before the first ask has even gone out, and it must read the
    // same as `loading`: "no rules yet" is a claim, and a screen that has not
    // asked anybody is in no position to make it (`docs/plans/argus.md` rule 9).
    if ((rulesStatus.value === 'idle' || rulesStatus.value === 'loading') && !rules.value.length) {
      return html`<p class="argus-room-waiting">Reading the rules…</p>`;
    }
    if (rulesStatus.value === 'failed') {
      return html`<p class="argus-room-waiting">
        <span class="argus-room-waiting-label">Not readable</span>
        ${rulesError.value}
      </p>`;
    }
    if (!rules.value.length) {
      return html`
        <p class="argus-room-blurb">
          No rules yet. A rule says what is worth watching: which ${this.entityNouns()}, which
          signals, when it counts, and how far off is too far. It reads back as one plain line.
        </p>
      `;
    }
    const shown = this.ordered(rules.value.filter((r) => this.visible(r)));
    const p = paginate(shown, this.page, this.pageSize);
    const selected = rules.value.find((r) => r.id === this.selectedId) ?? null;
    return html`
      ${this.renderToolbar()}
      ${rulesError.value && !selected
        ? html`<p class="argus-form-error" role="alert">${rulesError.value}</p>`
        : nothing}
      ${renderPager(p, 'rules', (page) => {
        this.page = page;
        // The first row, just below the pinned pager.
        void this.updateComplete.then(() => this.querySelector('.argus-ltable-wrap')?.scrollIntoView({ block: 'start' }));
      }, {
        value: this.pageSize,
        options: PAGE_SIZES,
        set: (size) => {
          this.pageSize = size;
          this.page = 0;
          try {
            localStorage.setItem(PAGE_SIZE_KEY, String(size));
          } catch {
            /* a remembered page size is a convenience */
          }
        },
      })}
      <div class="argus-ltable-wrap">
        <table class="argus-ltable">
          <colgroup>
            <col />
            <col class="argus-lcol-scope" />
            <col class="argus-lcol-found" />
            <col class="argus-lcol-sev-word" />
            <col class="argus-lcol-owner" />
            <col class="argus-lcol-when" />
            <col class="argus-lcol-switch" />
          </colgroup>
          <thead>
            <tr>
              ${this.sortHead('name', 'Rule')}
              ${this.sortHead('scope', 'Watches')}
              <th title="What the watcher found, at this version">Found</th>
              ${this.sortHead('severity', 'Severity', 'desc')}
              ${this.sortHead('owner', 'Owner')}
              ${this.sortHead('changed', 'Changed', 'desc')}
              ${this.sortHead('status', 'On', 'asc', 'argus-lswitch-head')}
            </tr>
          </thead>
          <tbody>
            ${shown.length
              ? repeat(p.items, (r) => r.id, (r) => this.renderRow(r))
              : html`<tr>
                  <td colspan="7" class="argus-lempty">Nothing here matches the search or filters.</td>
                </tr>`}
          </tbody>
        </table>
      </div>
      <!-- What the pointer carries while a rule is dragged to a folder. Always
           here and off screen: an element added at dragstart aborts the drag. -->
      <span class="argus-drag-tag" aria-hidden="true"><span class="argus-drag-dot"></span><span class="argus-drag-name"></span></span>
      <!-- The detail slides over the table rather than squeezing it, so no
           column ever moves. Esc, the ×, or the same row again closes it. -->
      ${selected ? this.renderDetail(selected) : nothing}
    `;
  }

  /**
   * The rows to draw, in a steady order: the last drawn order while nothing
   * that decides it has changed, re-sorted otherwise.
   */
  private ordered(visible: ArgusRule[]): ArgusRule[] {
    const ids = visible.map((r) => r.id).sort();
    const key = JSON.stringify([this.sort, ids]);
    if (this.drawnOrder?.key !== key) {
      this.drawnOrder = { key, ids: sortRules(visible, this.sort).map((r) => r.id) };
    }
    const byId = new Map(visible.map((r) => [r.id, r]));
    return this.drawnOrder.ids.map((id) => byId.get(id)).filter((r): r is ArgusRule => !!r);
  }

  /**
   * A column heading that sorts. The first click sorts the way the column is
   * usually read (`first`); the second reverses it; the third goes back to
   * newest first.
   */
  private sortHead(
    key: RuleSortKey,
    label: string,
    first: 'asc' | 'desc' = 'asc',
    cls = '',
  ): TemplateResult {
    const on = this.sort?.key === key ? this.sort.dir : null;
    const next = (): RuleSort =>
      !on ? { key, dir: first } : on === first ? { key, dir: first === 'asc' ? 'desc' : 'asc' } : null;
    return html`<th
      class="argus-lth-sort ${cls} ${on ? 'is-sorted' : ''}"
      aria-sort=${on === 'asc' ? 'ascending' : on === 'desc' ? 'descending' : 'none'}
    >
      <button type="button" @click=${() => this.refilter(() => (this.sort = next()))}>
        ${label}<span class="argus-lsort-caret" aria-hidden="true">${on === 'asc' ? '▲' : on === 'desc' ? '▼' : ''}</span>
      </button>
    </th>`;
  }

  /* ── what "Write a rule" asks first ───────────────────── */

  private renderChooser(): TemplateResult | typeof nothing {
    if (!this.choosing) return nothing;
    const close = (): void => {
      this.choosing = false;
    };
    const blocks = (): void => {
      this.choosing = false;
      this.blockEditing = 'new';
    };
    const simple = (): void => {
      this.choosing = false;
      this.start();
    };
    return html`
      <div
        class="argus-choose-scrim"
        @click=${(e: Event) => {
          if (e.target === e.currentTarget) close();
        }}
        @keydown=${(e: KeyboardEvent) => {
          if (e.key === 'Escape') close();
        }}
      >
        <div class="argus-choose" role="dialog" aria-modal="true" aria-labelledby="argus-choose-title">
          ${this.renderSourceStep()}
          <h3 id="argus-choose-title">How do you want to write it?</h3>
          <p>Both end as the same rule, with the same sentence. A simple rule opens in blocks later if it grows.</p>
          <div class="argus-choose-options">
            <button type="button" class="argus-choose-option is-recommended" @click=${blocks} ${focusOnce()}>
              <span class="argus-choose-glyph" aria-hidden="true">
                <span class="argus-b-hex"><span class="argus-b-in">A</span></span>
                <span class="argus-b-hex is-def"><span class="argus-b-in">B</span></span>
              </span>
              <span class="argus-choose-tag">Recommended</span>
              <strong>Build with blocks</strong>
              <small>Several signals, ranges, ALL / ANY groups, named conditions, whole brands.</small>
            </button>
            <button type="button" class="argus-choose-option" @click=${simple}>
              <span class="argus-choose-glyph" aria-hidden="true">
                <span class="argus-b-pill is-series">signal</span>
                <span class="argus-b-word">above</span>
                <span class="argus-b-pill is-num">250</span>
              </span>
              <span class="argus-choose-tag is-quiet">Quick</span>
              <strong>Simple form</strong>
              <small>One ${this.entityNoun()}, one signal, one threshold.</small>
            </button>
          </div>
          <div class="argus-choose-foot">
            <button type="button" class="dash-btn" @click=${close}>Cancel</button>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Which data the new rule watches. Shown only when there is a choice: with
   * the Live feature off, the server lists EOS alone and nothing is asked.
   */
  private renderSourceStep(): TemplateResult | typeof nothing {
    if (sources.value.length < 2) return nothing;
    return html`
      <h3>Which data does it watch?</h3>
      <div class="argus-choose-options argus-choose-sources" role="radiogroup" aria-label="Data source">
        ${sources.value.map((src) => {
          const on = src.key === this.newSource;
          return html`<button
            type="button"
            class="argus-choose-option ${on ? 'is-picked' : ''}"
            role="radio"
            aria-checked=${on ? 'true' : 'false'}
            data-source=${src.key}
            @click=${() => (this.newSource = src.key)}
          >
            <strong>${src.name}</strong>
            <small>${src.description}</small>
          </button>`;
        })}
      </div>
      <p class="argus-choose-note">
        A rule keeps its data source. EOS and Live name their signals differently and do not map to
        each other yet.
      </p>
    `;
  }

  /* ── the form ─────────────────────────────────────────── */

  private start(): void {
    const sourceKey = this.newSource || (sources.value[0]?.key ?? 'historical');
    this.draft = emptyDocument(sourceKey);
    this.draftName = '';
    this.editingId = null;
    this.formError = null;
    void loadEntities(sourceKey);
  }

  private edit(rule: ArgusRule): void {
    // A tree does not fit the Guided form's eight fields; it is edited as blocks.
    if (!rule.document || isTreeRule(rule)) return;
    const document = rule.document as ArgusRuleDocument;
    this.draft = { ...document, gates: [...(document.gates ?? [])] };
    this.draftName = rule.name;
    this.editingId = rule.id;
    this.formError = null;
    void loadEntities(document.source).then(() =>
      loadSeries(document.source, document.subject.entity),
    );
  }

  /**
   * Edit in blocks whenever the rule can be drawn as blocks - which is every
   * rule the server could lift. Saving writes a version 2 document as the next
   * version; the version 1 history stays as it was written.
   */
  private openEditor(rule: ArgusRule): void {
    if (rule.tree) this.blockEditing = rule;
    else if (!isTreeRule(rule)) this.edit(rule);
  }

  private close(): void {
    this.draft = null;
    this.editingId = null;
    this.formError = null;
  }

  private patch(change: Partial<ArgusRuleDocument>): void {
    if (!this.draft) return;
    this.draft = { ...this.draft, ...change };
  }

  /**
   * Deploy, or stop - back to draft.
   *
   * Offered to everyone, and refused by the database when it should be - which
   * is the point: a control hidden from somebody who may not use it teaches
   * nothing, while a refusal that names what was missing teaches exactly what.
   */
  private async changeStatus(
    rule: ArgusRule,
    status: 'deployed' | 'draft' | 'retired',
  ): Promise<void> {
    const error = await setStatus(rule.id, status);
    // A toast, because the list or the drawer may be scrolled anywhere; named,
    // because it is not beside the rule it is about.
    if (error) showToast('error', `${rule.name}: ${error}`, 0);
  }

  /**
   * Delete a rule. Soft underneath - its versions stay in the database for
   * whatever pointed at them - but gone from every screen. A deployed rule is
   * stopped first, which is a leader's call, and the database makes it. The
   * press-and-hold is the confirmation, as it is in the Catalog.
   */
  private async deleteRule(rule: ArgusRule): Promise<boolean> {
    const error = await removeRule(rule.id);
    if (error) {
      // The delete strip is at the end of a long page: a card under it is
      // below the fold, so the refusal is a toast that stays until closed.
      showToast('error', `${rule.name}: ${error}`, 0);
      return false;
    }
    if (this.selectedId === rule.id) this.selectedId = null;
    return true;
  }

  /** Delete from inside the editor, the one place it is offered: gone, the
   *  editor has nothing left to show and the list comes back. */
  private async deleteFromEditor(rule: ArgusRule): Promise<void> {
    if (!(await this.deleteRule(rule))) return;
    this.blockEditing = null;
    this.assistantDraft = null;
    this.close();
  }

  private async save(): Promise<void> {
    if (!this.draft) return;
    this.saving = true;
    this.formError = null;
    const answer = await saveRule(this.editingId, this.draftName.trim(), this.draft);
    this.saving = false;
    if (answer.ok) this.close();
    else this.formError = answer.error;
  }

  private renderGate(gate: ArgusGate, index: number): TemplateResult {
    return html`
      <div class="argus-gate-row">
        <select
          @change=${(e: Event) => this.editGate(index, { series: (e.target as HTMLSelectElement).value })}
        >
          ${seriesForEntity.value.map(
            (s) => html`<option value=${s.name} ?selected=${s.name === gate.series}>${s.name}</option>`,
          )}
        </select>
        <select
          @change=${(e: Event) =>
            this.editGate(index, { op: (e.target as HTMLSelectElement).value as ArgusGate['op'] })}
        >
          ${['>', '>=', '<', '<=', '==', '!='].map(
            (op) => html`<option value=${op} ?selected=${op === gate.op}>${op}</option>`,
          )}
        </select>
        <input
          type="number"
          .value=${String(gate.value)}
          @input=${(e: Event) =>
            this.editGate(index, { value: Number((e.target as HTMLInputElement).value) })}
        />
        <button type="button" class="dash-btn" @click=${() => this.removeGate(index)}>Remove</button>
      </div>
    `;
  }

  private editGate(index: number, change: Partial<ArgusGate>): void {
    if (!this.draft) return;
    const gates = this.draft.gates.map((g, i) => (i === index ? { ...g, ...change } : g));
    this.patch({ gates });
  }

  private removeGate(index: number): void {
    if (!this.draft) return;
    this.patch({ gates: this.draft.gates.filter((_, i) => i !== index) });
  }

  private addGate(): void {
    if (!this.draft) return;
    const first = seriesForEntity.value[0]?.name ?? '';
    this.patch({ gates: [...this.draft.gates, { series: first, op: '>', value: 0 }] });
  }

  private renderForm(document: ArgusRuleDocument): TemplateResult {
    const noun = this.entityNoun();
    const sentence = ruleSentence(document, this.entityName(document.subject.entity));
    const editing = rules.value.find((r) => r.id === this.editingId) ?? null;
    return html`
      <div class="argus-form">
        <div class="argus-form-head">
          <h2>${this.editingId ? 'Change a rule' : 'Write a rule'}</h2>
          <button type="button" class="dash-btn" @click=${() => this.close()}>Cancel</button>
        </div>

        <!-- The sentence first and always: it is what the rule *is*, and the
             fields below are only a way of reaching it. -->
        <p class="argus-sentence" aria-live="polite">
          ${sentence || `Choose a ${noun} and a series, and the rule will read itself back here.`}
        </p>

        <label class="argus-field">
          <span>Name</span>
          <input
            type="text"
            maxlength="140"
            placeholder="What you would call this in a meeting"
            .value=${this.draftName}
            @input=${(e: Event) => {
              this.draftName = (e.target as HTMLInputElement).value;
            }}
          />
        </label>

        <div class="argus-field-row">
          <label class="argus-field">
            <span>Which ${noun}</span>
            <select
              @change=${(e: Event) => {
                const entity = (e.target as HTMLSelectElement).value;
                this.patch({ subject: { entity, series: '' } });
                void loadSeries(document.source, entity);
              }}
            >
              <option value="" ?selected=${!document.subject.entity}>Choose…</option>
              ${entities.value.map(
                (e) =>
                  html`<option value=${e.id} ?selected=${e.id === document.subject.entity}>
                    ${e.name}
                  </option>`,
              )}
            </select>
          </label>

          <label class="argus-field">
            <span>Which series</span>
            <select
              ?disabled=${!document.subject.entity}
              @change=${(e: Event) =>
                this.patch({
                  subject: {
                    entity: document.subject.entity,
                    series: (e.target as HTMLSelectElement).value,
                  },
                })}
            >
              <option value="" ?selected=${!document.subject.series}>Choose…</option>
              ${seriesForEntity.value.map(
                (s) => html`<option value=${s.name} ?selected=${s.name === document.subject.series}>
                  ${s.name}${s.unit ? ` (${s.unit})` : ''}
                </option>`,
              )}
            </select>
          </label>
        </div>

        <fieldset class="argus-field">
          <legend>When it fires</legend>
          ${SHAPE_WORDS.map(
            (shape) => html`
              <label class="argus-shape">
                <input
                  type="radio"
                  name="shape"
                  .checked=${document.shape === shape.id}
                  @change=${() => this.patch({ shape: shape.id as ArgusShape })}
                />
                <span>
                  <strong>${shape.label}</strong>
                  <small>${shape.hint}</small>
                </span>
              </label>
            `,
          )}
        </fieldset>

        <div class="argus-field-row">
          <label class="argus-field">
            <span>Window</span>
            <select
              @change=${(e: Event) => this.patch({ window: (e.target as HTMLSelectElement).value })}
            >
              ${WINDOWS.map(
                (w) => html`<option value=${w} ?selected=${w === document.window}>${w}</option>`,
              )}
            </select>
          </label>

          ${document.shape === 'window'
            ? html`<label class="argus-field">
                <span>Of the</span>
                <select
                  @change=${(e: Event) =>
                    this.patch({
                      aggregate: (e.target as HTMLSelectElement).value as ArgusAggregate,
                    })}
                >
                  ${['mean', 'max', 'min', 'p95', 'sum'].map(
                    (a) =>
                      html`<option value=${a} ?selected=${a === (document.aggregate ?? 'mean')}>
                        ${a}
                      </option>`,
                  )}
                </select>
              </label>`
            : nothing}

          ${document.shape === 'silence'
            ? nothing
            : html`
                <label class="argus-field">
                  <span>Alert when</span>
                  <select
                    @change=${(e: Event) =>
                      this.patch({
                        test: {
                          direction: (e.target as HTMLSelectElement).value as 'above' | 'below',
                          value: document.test?.value ?? 0,
                        },
                      })}
                  >
                    <option value="above" ?selected=${document.test?.direction !== 'below'}>
                      above
                    </option>
                    <option value="below" ?selected=${document.test?.direction === 'below'}>
                      below
                    </option>
                  </select>
                </label>
                <label class="argus-field">
                  <span>This value</span>
                  <input
                    type="number"
                    .value=${String(document.test?.value ?? 0)}
                    @input=${(e: Event) =>
                      this.patch({
                        test: {
                          direction: document.test?.direction ?? 'above',
                          value: Number((e.target as HTMLInputElement).value),
                        },
                      })}
                  />
                </label>
              `}
        </div>

        <label class="argus-field argus-field-narrow">
          <span>How loud</span>
          <select
            @change=${(e: Event) =>
              this.patch({ severity: (e.target as HTMLSelectElement).value as ArgusSeverity })}
          >
            ${(
              [
                ['info', 'Info - worth knowing'],
                ['watch', 'Watch - look into it'],
                ['critical', 'Critical - act on it'],
              ] as const
            ).map(
              ([id, label]) =>
                html`<option value=${id} ?selected=${document.severity === id}>${label}</option>`,
            )}
          </select>
        </label>

        <!-- The ladder, shown rather than hidden: the team should see where
             this goes, and why it cannot go there yet. -->
        <p class="argus-room-waiting">
          <span class="argus-room-waiting-label">Fixed number only</span>
          Comparing against its own normal, against sister ${noun}s, or against a model
          needs a watcher to compute the comparison, and none is connected.
        </p>

        <fieldset class="argus-field">
          <legend>
            Only count when
            <small>Without a gate, this rule also watches while nothing is running.</small>
          </legend>
          ${document.gates.map((gate, index) => this.renderGate(gate, index))}
          <button
            type="button"
            class="dash-btn"
            ?disabled=${!seriesForEntity.value.length}
            @click=${() => this.addGate()}
          >
            Add a condition
          </button>
        </fieldset>

        ${this.formError
          ? html`<p class="argus-form-error" role="alert">${this.formError}</p>`
          : nothing}

        <div class="argus-form-foot">
          <span class="argus-room-waiting">
            Saved as a draft. Deploying a rule - putting it on a schedule - is not built yet.
          </span>
          <button
            type="button"
            class="btn btn-primary"
            ?disabled=${this.saving || !this.draftName.trim() || !document.subject.series}
            @click=${() => void this.save()}
          >
            ${this.saving ? 'Saving…' : this.editingId ? 'Save as a new version' : 'Save rule'}
          </button>
        </div>
        ${editing && this.mayAuthor()
          ? html`<argus-delete-strip
              .rule=${editing}
              @argus-delete=${(e: CustomEvent<{ rule: ArgusRule }>) => void this.deleteFromEditor(e.detail.rule)}
            ></argus-delete-strip>`
          : nothing}
        ${rulesError.value ? html`<p class="argus-form-error" role="alert">${rulesError.value}</p>` : nothing}
      </div>
    `;
  }

  override render(): TemplateResult {
    if (this.draft) return this.renderForm(this.draft);
    if (this.blockEditing) {
      const rule = this.blockEditing === 'new' ? null : this.blockEditing;
      return html`
        <div class="argus-rules is-wide">
          <!-- The editor's own head is the title: the name, large, and its
               description under it. -->
          <!-- A new editor per draft: it reads its document once, when it
               opens, so a second Assistant draft handed to an editor already
               open would otherwise leave the first one on screen. -->
          ${keyed(
            this.assistantDraft ?? rule?.id ?? 'new',
            html`<argus-rule-editor
              .rule=${rule}
              .draft=${this.assistantDraft}
              .sourceKey=${this.newSource || (sources.value[0]?.key ?? 'historical')}
              @argus-editor-close=${() => {
                this.blockEditing = null;
                this.assistantDraft = null;
                void this.takeOpenRule();
              }}
              @argus-editor-saved=${(e: CustomEvent<{ rule: ArgusRule }>) => {
                // Saved, and the editor stays: it now edits the rule as stored.
                this.blockEditing = e.detail.rule;
                this.assistantDraft = null;
              }}
            ></argus-rule-editor>`,
          )}
          ${rule && !this.assistantDraft && this.mayAuthor()
            ? html`<argus-delete-strip
                .rule=${rule}
                @argus-delete=${(e: CustomEvent<{ rule: ArgusRule }>) => void this.deleteFromEditor(e.detail.rule)}
              ></argus-delete-strip>`
            : nothing}
          ${rulesError.value ? html`<p class="argus-form-error" role="alert">${rulesError.value}</p>` : nothing}
        </div>
      `;
    }
    return html`
      <div class="argus-rules is-wide is-railed">
        <argus-team-rail
          .place=${this.currentPlace()}
          .dragging=${this.dragging}
          ?canFile=${this.mayAuthor()}
          @argus-place=${(e: CustomEvent<{ place: string }>) => this.setPlace(e.detail.place)}
          @argus-file-rule=${(e: CustomEvent<{ rule: ArgusRule; folderId: string | null }>) => {
            this.dragging = null;
            void this.fileRule(e.detail.rule, e.detail.folderId);
          }}
        ></argus-team-rail>
        <div class="argus-rules-main">
          ${this.renderPlaceHead()} ${this.renderNoTeam()}
          ${this.renderList()} ${this.renderChooser()} ${this.renderRowMenu()}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'argus-rules-room': ArgusRulesRoom;
  }
}
